/**
 * Flat vector illustrations used by the template gallery. They ship with the code (no stock
 * photos), are previewed in the browser as SVG and uploaded as PNG assets when a gallery
 * template is used, so users can replace them like any other image.
 */

import { arena, concert, crestBlack, crestBlue, crestGreen, crestRed, stadium } from './sports-art';
import { isPhotoId, photoUrl, type PhotoId } from './photos';

const W = 1200;
const H = 1200;

function svg(bg: [string, string], body: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><defs><linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${bg[0]}"/><stop offset="1" stop-color="${bg[1]}"/></linearGradient></defs><rect width="${W}" height="${H}" fill="url(#bg)"/>${body}</svg>`;
}

const glow = (cx: number, cy: number, r: number, o = 0.22) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#FFFFFF" opacity="${o}"/>`;

export const ILLUSTRATIONS = {
  sneaker: svg(
    ['#FDE68A', '#F97316'],
    `${glow(980, 220, 200, 0.25)}${glow(160, 1040, 280, 0.18)}
<ellipse cx="600" cy="900" rx="430" ry="44" fill="#7C2D12" opacity="0.25"/>
<path d="M190 800 C 210 660 300 590 420 580 C 520 572 560 500 600 420 C 640 350 720 340 760 400 C 820 490 900 550 1000 600 C 1060 630 1080 700 1060 770 C 1040 840 980 860 900 860 L 280 860 C 220 860 182 840 190 800 Z" fill="#F8FAFC"/>
<path d="M190 800 L 1060 800 C 1062 830 1040 875 960 880 L 270 880 C 210 880 186 850 190 800 Z" fill="#4F46E5"/>
<path d="M610 460 L 700 660 M 650 445 L 760 640 M 700 440 L 820 620" stroke="#4F46E5" stroke-width="18" stroke-linecap="round"/>
<path d="M300 720 C 450 660 620 680 760 740" stroke="#F97316" stroke-width="26" fill="none" stroke-linecap="round"/>`,
  ),
  headphones: svg(
    ['#C4B5FD', '#6D28D9'],
    `${glow(1000, 200, 220)}${glow(200, 1000, 260, 0.15)}
<path d="M300 700 C 300 420 440 280 600 280 C 760 280 900 420 900 700" stroke="#1E1B4B" stroke-width="56" fill="none" stroke-linecap="round"/>
<rect x="220" y="620" width="190" height="300" rx="80" fill="#F472B6"/>
<rect x="790" y="620" width="190" height="300" rx="80" fill="#F472B6"/>
<rect x="260" y="660" width="110" height="220" rx="50" fill="#1E1B4B" opacity="0.25"/>
<rect x="830" y="660" width="110" height="220" rx="50" fill="#1E1B4B" opacity="0.25"/>`,
  ),
  house: svg(
    ['#BAE6FD', '#0EA5E9'],
    `${glow(980, 220, 140, 0.5)}
<rect x="0" y="900" width="1200" height="300" fill="#16A34A"/>
<polygon points="250,560 600,290 950,560" fill="#B91C1C"/>
<rect x="300" y="550" width="600" height="380" fill="#FEF3C7"/>
<rect x="540" y="720" width="120" height="210" rx="10" fill="#92400E"/>
<rect x="360" y="620" width="120" height="110" rx="8" fill="#38BDF8"/>
<rect x="720" y="620" width="120" height="110" rx="8" fill="#38BDF8"/>
<rect x="760" y="330" width="70" height="160" fill="#7F1D1D"/>
<circle cx="140" cy="820" r="110" fill="#15803D"/><rect x="128" y="860" width="24" height="80" fill="#713F12"/>`,
  ),
  food: svg(
    ['#FED7AA', '#EA580C'],
    `${glow(200, 200, 200)}${glow(1020, 1020, 240, 0.15)}
<ellipse cx="600" cy="820" rx="420" ry="70" fill="#7C2D12" opacity="0.25"/>
<path d="M220 600 L 980 600 C 980 820 820 900 600 900 C 380 900 220 820 220 600 Z" fill="#F8FAFC"/>
<ellipse cx="600" cy="600" rx="380" ry="70" fill="#FDE68A"/>
<circle cx="480" cy="570" r="70" fill="#16A34A"/><circle cx="640" cy="560" r="80" fill="#DC2626"/>
<circle cx="760" cy="590" r="55" fill="#F59E0B"/><circle cx="560" cy="610" r="45" fill="#65A30D"/>
<path d="M820 330 L 700 560 M 880 360 L 740 570" stroke="#78350F" stroke-width="18" stroke-linecap="round"/>`,
  ),
  travel: svg(
    ['#FBCFE8', '#F97316'],
    `<circle cx="820" cy="380" r="150" fill="#FEF3C7"/>
<polygon points="0,900 330,470 620,900" fill="#7C3AED"/>
<polygon points="380,900 760,400 1200,900" fill="#5B21B6"/>
<polygon points="680,520 760,400 840,520 790,500 760,540 720,500" fill="#F8FAFC"/>
<rect x="0" y="880" width="1200" height="320" fill="#0EA5E9"/>
<path d="M0 960 C 200 930 400 990 600 960 C 800 930 1000 990 1200 960" stroke="#E0F2FE" stroke-width="12" fill="none"/>
<path d="M160 260 l 40 -20 l 40 20 M 300 200 l 30 -15 l 30 15" stroke="#7C2D12" stroke-width="10" fill="none" stroke-linecap="round"/>`,
  ),
  laptop: svg(
    ['#A7F3D0', '#059669'],
    `${glow(1000, 220, 220)}${glow(180, 980, 240, 0.15)}
<rect x="270" y="340" width="660" height="430" rx="28" fill="#0F172A"/>
<rect x="300" y="370" width="600" height="370" rx="10" fill="#38BDF8"/>
<rect x="340" y="420" width="260" height="34" rx="17" fill="#F8FAFC"/>
<rect x="340" y="480" width="380" height="22" rx="11" fill="#E0F2FE"/>
<rect x="340" y="520" width="320" height="22" rx="11" fill="#E0F2FE"/>
<rect x="340" y="600" width="170" height="70" rx="35" fill="#FACC15"/>
<path d="M200 790 L 1000 790 L 1060 860 L 140 860 Z" fill="#334155"/>`,
  ),
  gift: svg(
    ['#FECACA', '#DC2626'],
    `${glow(1000, 200, 200)}${glow(180, 1020, 260, 0.15)}
<rect x="330" y="520" width="540" height="400" rx="20" fill="#F8FAFC"/>
<rect x="290" y="420" width="620" height="140" rx="20" fill="#FFFFFF"/>
<rect x="560" y="420" width="80" height="500" fill="#FACC15"/>
<path d="M600 420 C 520 300 380 320 420 390 C 450 440 560 430 600 420 C 640 430 750 440 780 390 C 820 320 680 300 600 420 Z" fill="#FACC15"/>`,
  ),
  fitness: svg(
    ['#BFDBFE', '#2563EB'],
    `${glow(980, 240, 220)}${glow(200, 1000, 260, 0.15)}
<rect x="330" y="560" width="540" height="80" rx="40" fill="#0F172A"/>
<rect x="210" y="430" width="100" height="340" rx="30" fill="#F97316"/>
<rect x="890" y="430" width="100" height="340" rx="30" fill="#F97316"/>
<rect x="120" y="480" width="80" height="240" rx="26" fill="#FB923C"/>
<rect x="1000" y="480" width="80" height="240" rx="26" fill="#FB923C"/>`,
  ),
  coffee: svg(
    ['#E7E5E4', '#78716C'],
    `${glow(1000, 220, 220, 0.3)}${glow(180, 1000, 260, 0.2)}
<ellipse cx="560" cy="900" rx="380" ry="50" fill="#292524" opacity="0.25"/>
<path d="M300 480 L 820 480 L 780 860 C 770 900 740 920 700 920 L 420 920 C 380 920 350 900 340 860 Z" fill="#F8FAFC"/>
<path d="M820 560 C 960 560 960 760 800 760" stroke="#F8FAFC" stroke-width="44" fill="none"/>
<ellipse cx="560" cy="490" rx="250" ry="34" fill="#78350F"/>
<path d="M460 380 C 420 330 500 300 460 240 M 580 380 C 540 330 620 300 580 240 M 700 380 C 660 330 740 300 700 240" stroke="#FFFFFF" stroke-width="16" fill="none" stroke-linecap="round" opacity="0.8"/>`,
  ),
  abstract: svg(
    ['#1E1B4B', '#7C3AED'],
    `<circle cx="300" cy="320" r="240" fill="#F472B6" opacity="0.85"/>
<circle cx="860" cy="760" r="320" fill="#22D3EE" opacity="0.7"/>
<rect x="520" y="200" width="380" height="380" rx="80" fill="#FACC15" opacity="0.85" transform="rotate(18 710 390)"/>
<circle cx="380" cy="900" r="140" fill="#FFFFFF" opacity="0.2"/>`,
  ),
  stadium,
  arena,
  concert,
  'crest-red': crestRed,
  'crest-blue': crestBlue,
  'crest-green': crestGreen,
  'crest-black': crestBlack,
} as const;

export type IllustrationId = keyof typeof ILLUSTRATIONS;

/** Image `src` used by gallery documents until the illustration is uploaded. */
export const GALLERY_SRC_PREFIX = 'gallery:';

export function illustrationDataUrl(id: string): string | null {
  if (!Object.prototype.hasOwnProperty.call(ILLUSTRATIONS, id)) return null;
  const svgText = (ILLUSTRATIONS as Record<string, string>)[id];
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
}

/** Any picture a gallery document may refer to as `gallery:<id>`: an illustration or a photo. */
export type GalleryImageId = IllustrationId | PhotoId;

export function isGalleryImageId(id: string): id is GalleryImageId {
  return isPhotoId(id) || Object.prototype.hasOwnProperty.call(ILLUSTRATIONS, id);
}

/** Browser URL of a gallery picture: inline SVG for illustrations, a static file for photos. */
export function galleryImageUrl(id: string): string | null {
  return isPhotoId(id) ? photoUrl(id) : illustrationDataUrl(id);
}
